import { Grid, withStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography/index'
import { DeleteForever } from '@material-ui/icons'
import AnnouncementIcon from '@material-ui/icons/Announcement'
import CreateIcon from '@material-ui/icons/Create'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'
import { dataTableActions, getStyles } from 'isotope-client'
import PropTypes from 'prop-types'
import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { compose } from 'redux'
import Button from '../../../../components/Button'
import FormButton from '../../../../components/form/FormButton'
import { DialogContext } from '../../../../components/layout/dialog'
import { ActionPanelContext, DetailsPanelContext } from '../../../../components/layout/rightPanels'
import { useSnackbar } from '../../../../components/layout/snackbar/SnackbarContext'
import { DEFAULT_LABEL, STATUTS_TACHE, TACHE_TYPE_ENTITE, TYPE_DIALOG } from '../../../../utils/constants'
import { injectTypeTacheValueList } from '../../../common/valueLists/valueListInjectors'
import Detail from '../../planning/component/detail/Detail'
import { detailTaskStyles } from '../../scheduler/services/task.styles'
import { injectTaskUpdateForm } from '../../tourPlaine/service/taskUpdateFormInjector'
import ContenuPopinSuppressionErrorTache from '../component/planification/ContenuPopinSuppressionErrorTache'
import ContenuPopinSuppressionTache, { formName } from '../component/planification/ContenuPopinSuppressionTache'
import { useGestionTacheContext } from '../GestionTacheProvider'
import { deleteTasks as deleteTasksApi } from '../gestionTachesApi'
import { injectSuiviDeTache } from '../injectors/suiviDeTacheInjector'
import { getCurrentTache, getDetailPanelType } from '../services/tache/tacheSelector'
import { PANEL_TYPE, PLANIFICATION_TABLE_NAME } from '../utils/constants'
import { getDetailSections } from '../utils/tacheUtils'

const styles = () => getStyles({
	container: {
		margin: '1.5em'
	},
	form: {
		width: '100%',
		margin: 15
	},
	...detailTaskStyles
})

/**
 * Injecteur pour l'affichage du panel de détail de tache
 */
export const injectTachePanel = (WrappedComponent) => {
	const TacheDetailInjector = ({ classes, panelType, currentTache, suiviDeTache, typeTacheList, refreshTable, resetSelectedRows, openTaskUpdateForm, ...props }) => {

		const { updatePanel: updateDetailsPanel } = React.useContext(DetailsPanelContext)
		const { closePanel: closePanelAction } = React.useContext(ActionPanelContext)
		const { onglet, jourSelection, isResultat } = useGestionTacheContext()
		const { openDialog, closeDialog } = React.useContext(DialogContext)
		const { snackError, snackSuccess } = useSnackbar()
		const intl = useIntl()

		const deleteTasks = () => {
			if (currentTache.statut === STATUTS_TACHE.TERMINEE) {
				openDialog(
					<span className={classes.spanTitlePopin}>
					<WarningRoundedIcon className={classes.error} />
					<FormattedMessage id="gestionTaches.toolbar.actions.delete.popin.error.title" />
				</span>,
					<ContenuPopinSuppressionErrorTache tachesEnErreur={currentTache} />,
					[], TYPE_DIALOG.ACTION, 'actions.close'
				)
			} else {
				openDialog(
					{ id: 'gestionTaches.toolbar.actions.delete.popin.validation.title' },
					<ContenuPopinSuppressionTache tachesRecurrentes={currentTache.recurrence && currentTache.recurrence.length > 0 ? currentTache : []} onSubmit={handleDeleteTasks} />,
					[
						<FormButton
							type="primary"
							formName={formName}
						>
							<FormattedMessage id="actions.delete" />
						</FormButton>
					], TYPE_DIALOG.ACTION
				)
			}
		}

		const handleDeleteTasks = (values) => {
			deleteTasksApi([currentTache.id], values.typeSuppression).then(() => {
				refreshTable()
				resetSelectedRows()
				closeDialog()
				snackSuccess({ id: 'gestionTaches.toolbar.actions.delete.success' })
			}).catch(() => {
				closeDialog()
				snackError({ id: 'gestionTaches.toolbar.actions.delete.error' })
			})
		}

		React.useEffect(() => {
			closePanelAction()

			if (currentTache && panelType === PANEL_TYPE.TACHE_DETAIL) {
				const nomTache = (typeTacheList.find(type => type.code === currentTache.type) || {}).label || DEFAULT_LABEL
				const title = <span>
					{currentTache.culture
						? <FormattedMessage id="gestionTaches.ecranPlanification.panels.titre.details2"
						                    values={{
							                    nomTache,
							                    nomCulture: currentTache.culture.nom
						                    }} />
						: <FormattedMessage id="gestionTaches.ecranPlanification.panels.titre.details1"
						                    values={{
							                    nomTache
						                    }} />
					}
					</span>

				const buttons = isResultat ? [] : [
					<Button type="secondary"
					        onClick={() => {
						        suiviDeTache(currentTache, jourSelection)
					        }}
					        startIcon={<AnnouncementIcon color="currentColor" />}>
						<FormattedMessage id="gestionTaches.ecranPlanification.panels.buttons.suivi" />
					</Button>
				]

				let lieu = intl.formatMessage({ id: 'enums.typeEntityAndValue.SURFACE' }, { planche: currentTache.assolement?.planche.nom })
				if (currentTache.assolement == null && currentTache.bloc != null) {
					lieu = intl.formatMessage({ id: 'enums.typeEntityAndValue.BLOC' }, { bloc: currentTache.bloc.nom })
				} else if (currentTache.typeEntity === TACHE_TYPE_ENTITE.SURFACE) {
					lieu = intl.formatMessage({ id: 'enums.typeEntityAndValue.SURFACE' }, { planche: currentTache.planche?.nom })
				} else if (currentTache.assolement == null) {
					lieu = intl.formatMessage({ id: 'enums.typeEntityAndValue.FERME' })
				}

				const content = <Grid container alignContent="flex-start">
					{!isResultat && <Grid container alignItems="center" justify="center" style={{ height: 50 }}>
						<Button
							type="primary"
							variant="text"
							withBorder={false}
							keepCase
							onClick={() => openTaskUpdateForm({
								...currentTache,
								materiels: currentTache.materiels.map(m => m.nom),
								lieu: lieu,
								plannedDate: currentTache.dateDebutPlanification
							}, () => {
								refreshTable()
								resetSelectedRows()
							})}
						>
							<Grid item container direction="row" justify="center" alignItems="center" style={{ gap: 10 }}>
								<CreateIcon color="primary" />
								<Typography color="primary">
									<FormattedMessage id="tourPlaine.form.updateTask.updateAction" />
								</Typography>
							</Grid>
						</Button>
						<Button
							type="primary"
							variant="text"
							withBorder={false}
							keepCase
							onClick={deleteTasks}
						>
							<Grid item container direction="row" justify="center" alignItems="center" style={{ gap: 10 }}>
								<DeleteForever color="primary" />
								<Typography color="primary">
									<FormattedMessage id="tourPlaine.form.updateTask.deleteAction" />
								</Typography>
							</Grid>
						</Button>
					</Grid>}
					{/* Contournement pour afficher les infos de la solution sur l'onglet planification */}
					<Detail sections={getDetailSections(currentTache, currentTache.solutionTache, true, classes, intl)} />
				</Grid>

				return updateDetailsPanel(
					title,
					content,
					buttons
				)
			}

		}, [currentTache, panelType, onglet, resetSelectedRows])

		return <WrappedComponent {...props} />
	}

	TacheDetailInjector.propTypes = {
		panelType: PropTypes.string,
		currentTache: PropTypes.object,
		typeTacheList: PropTypes.array
	}

	const mapStateToProps = state => ({
		currentTache: getCurrentTache(state),
		panelType: getDetailPanelType(state)
	})

	const actions = {
		refreshTable: () => dataTableActions.refresh(PLANIFICATION_TABLE_NAME, []),
		resetSelectedRows: () => dataTableActions.selectRows(PLANIFICATION_TABLE_NAME, [])
	}

	return compose(
		injectSuiviDeTache,
		injectTaskUpdateForm,
		injectTypeTacheValueList,
		connect(mapStateToProps, actions),
		withStyles(styles)
	)(TacheDetailInjector)
}
